import {
  BuildingsIcon,
  FileIcon,
  FileTextIcon,
  FolderSimpleIcon,
  FolderOpenIcon,
  ImageIcon,
  MicrosoftExcelLogoIcon,
  MicrosoftPowerpointLogoIcon,
  MicrosoftWordLogoIcon,
  NotePencilIcon,
  UsersThreeIcon,
  CloudIcon,
  BooksIcon
} from '@phosphor-icons/react';
import { DocumentHubNode } from '../types';

interface FileTypeIconProps {
  mimeType?: string | null;
  filename?: string | null;
  size?: number;
  className?: string;
}

/** File icon used in Document Hub lists — Excel/Word/PDF/etc from mime or extension. */
export function FileTypeIcon({ mimeType, filename, size = 18, className }: FileTypeIconProps) {
  const mt = mimeType ?? '';
  const ext = filename?.split('.').pop()?.toLowerCase() ?? '';
  const iconClass = className ?? 'text-content-default shrink-0';
  if (mt.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
    return <ImageIcon size={size} weight="light" className={iconClass} />;
  }
  if (mt === 'application/pdf' || ext === 'pdf') {
    return <FileTextIcon size={size} weight="light" className={iconClass} />;
  }
  if (mt.includes('wordprocessingml') || mt === 'application/msword' || ['doc', 'docx'].includes(ext)) {
    return <MicrosoftWordLogoIcon size={size} weight="light" className={iconClass} />;
  }
  if (mt.includes('spreadsheetml') || mt === 'application/vnd.ms-excel' || ['xls', 'xlsx', 'csv'].includes(ext)) {
    return <MicrosoftExcelLogoIcon size={size} weight="light" className={iconClass} />;
  }
  if (mt.includes('presentationml') || mt === 'application/vnd.ms-powerpoint' || ['ppt', 'pptx'].includes(ext)) {
    return <MicrosoftPowerpointLogoIcon size={size} weight="light" className={iconClass} />;
  }
  return <FileIcon size={size} weight="light" className={iconClass} />;
}

interface NodeIconProps {
  node: DocumentHubNode;
  size?: number;
  /** Show open-folder variant (folders only) */
  open?: boolean;
  /** Render a filled folder icon — use to signal the folder contains items of interest */
  filled?: boolean;
}

export default function NodeIcon({ node, size = 18, open = false, filled = false }: NodeIconProps) {
  if (node.type === 'folder') {
    if (node.folder_type === 'organisation') {
      return <BuildingsIcon size={size} weight="light" className="text-content-default shrink-0" />;
    }
    if (node.folder_type === 'my_uploads') {
      return <BooksIcon size={size} weight="light" className="text-content-default shrink-0" />;
    }
    if (node.folder_type === 'my_drafts') {
      return <NotePencilIcon size={size} weight="light" className="text-content-default shrink-0" />;
    }
    if (node.folder_type === 'group') {
      return <UsersThreeIcon size={size} weight="light" className="text-content-default shrink-0" />;
    }
    if (node.folder_type === 'sharepoint') {
      return <CloudIcon size={size} weight="light" className="text-content-default shrink-0" />;
    }
    if (filled) {
      return <FolderSimpleIcon size={size} weight="fill" className="text-secondary-blue/35 shrink-0" />;
    }
    return open ? (
      <FolderOpenIcon size={size} weight="light" className="text-content-default shrink-0" />
    ) : (
      <FolderSimpleIcon size={size} weight="light" className="text-content-default shrink-0" />
    );
  }

  return <FileTypeIcon mimeType={node.mime_type} filename={node.name} size={size} />;
}
